"use client";

import { useParams, usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Bug,
  Check,
  Copy,
  Globe,
  Lightbulb,
  Lock,
  MessageSquarePlus,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { SupportImageUploader } from "@/app/components/support/SupportImageUploader";
import { SUPPORT_CARD_CLASS as CARD_CLASS } from "@/app/components/support/supportUi";
import { useAuthState } from "@/hooks/useAuthState";
import {
  supportApi,
  type SupportTicketCategory,
  type SupportTicketVisibility,
} from "@/lib/api";
import { showToast } from "@/app/components/ui/Toast";

const CATEGORY_OPTIONS: Array<{
  value: SupportTicketCategory;
  label: string;
  href: string;
  hint: string;
  icon: LucideIcon;
}> = [
  {
    value: "ISSUE",
    label: "แจ้งปัญหา / บั๊ก",
    href: "/support/new/issue",
    hint: "แอปใช้งานผิดปกติ หรือเกิดข้อผิดพลาด",
    icon: Bug,
  },
  {
    value: "REQUEST",
    label: "ขอเพิ่มฟีเจอร์",
    href: "/support/new/request",
    hint: "อยากได้ความสามารถใหม่ในระบบ",
    icon: Sparkles,
  },
  {
    value: "SUGGESTION",
    label: "ข้อเสนอแนะ",
    href: "/support/new/suggestion",
    hint: "แนวคิดปรับปรุงประสบการณ์ใช้งาน",
    icon: Lightbulb,
  },
];

const VISIBILITY_OPTIONS: Array<{
  value: SupportTicketVisibility;
  label: string;
  hint: string;
  icon: typeof Globe;
}> = [
  {
    value: "PUBLIC",
    label: "สาธารณะ",
    hint: "แสดงบนบอร์ด ทุกคนอ่านและตอบกลับได้",
    icon: Globe,
  },
  {
    value: "PRIVATE",
    label: "ส่วนตัว",
    hint: "เห็นเฉพาะคุณกับทีมงาน ต้องระบุอีเมล",
    icon: Lock,
  },
];

const CATEGORY_FROM_SEGMENT: Record<string, SupportTicketCategory> = {
  issue: "ISSUE",
  request: "REQUEST",
  suggestion: "SUGGESTION",
};

function readEmailFromStorage(): string {
  if (typeof window === "undefined") return "";
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "";
    const u = JSON.parse(raw) as { email?: string };
    return typeof u.email === "string" ? u.email : "";
  } catch {
    return "";
  }
}

function submitErrorToThai(message: string | null | undefined): string {
  if (!message?.trim()) return "ส่งตั๋วไม่สำเร็จ กรุณาลองอีกครั้ง";
  const raw = message.trim();
  if (/[\u0E00-\u0E7F]/.test(raw)) return raw;

  const m = raw.toLowerCase();
  if (/invalid response|bad gateway|internal server/.test(m)) {
    return "ได้รับข้อมูลจากเซิร์ฟเวอร์ไม่ถูกต้อง กรุณาลองใหม่";
  }
  if (/network|fetch failed|failed to fetch|timeout/.test(m)) {
    return "เชื่อมต่อไม่สำเร็จ กรุณาลองใหม่";
  }
  if (/unauthorized|401|forbidden|403/.test(m)) {
    return "กรุณาเข้าสู่ระบบก่อนส่งตั๋ว";
  }
  if (/title|subject/.test(m) && /short|min|length/.test(m)) {
    return "หัวข้อสั้นเกินไป กรุณาใส่อย่างน้อย 3 ตัวอักษร";
  }
  if (/body|description|content/.test(m) && /short|min|length/.test(m)) {
    return "รายละเอียดสั้นเกินไป กรุณาใส่อย่างน้อย 10 ตัวอักษร";
  }
  if (/email/.test(m) && /required|invalid/.test(m)) {
    return "กรุณาระบุอีเมลที่ถูกต้อง";
  }
  return "ส่งตั๋วไม่สำเร็จ กรุณาลองอีกครั้ง";
}

export default function NewSupportTicketPage() {
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const categoryParam =
    typeof params.category === "string" ? params.category.toLowerCase() : "";
  const category: SupportTicketCategory =
    CATEGORY_FROM_SEGMENT[categoryParam] ?? "ISSUE";
  const { isLoggedIn } = useAuthState();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [visibility, setVisibility] =
    useState<SupportTicketVisibility>("PUBLIC");
  const [contactEmail, setContactEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [privateLink, setPrivateLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!CATEGORY_FROM_SEGMENT[categoryParam]) {
      router.replace("/support/new/issue");
    }
  }, [categoryParam, router]);

  useEffect(() => {
    if (isLoggedIn) {
      const email = readEmailFromStorage();
      if (email) setContactEmail(email);
    }
  }, [isLoggedIn]);

  const activeCategory = useMemo(
    () =>
      CATEGORY_OPTIONS.find((c) => c.value === category) ?? CATEGORY_OPTIONS[0],
    [category],
  );

  const emailRequired = visibility === "PRIVATE";
  const titleOk = title.trim().length >= 3;
  const bodyOk = body.trim().length >= 10;
  const canSubmit = useMemo(() => {
    if (!titleOk || !bodyOk) return false;
    if (emailRequired && !contactEmail.trim()) return false;
    return true;
  }, [titleOk, bodyOk, emailRequired, contactEmail]);

  async function copyPrivateLink() {
    if (!privateLink) return;
    try {
      await navigator.clipboard.writeText(privateLink);
      setCopied(true);
      showToast("คัดลอกแล้ว");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await supportApi.create({
        title: title.trim(),
        body: body.trim(),
        category,
        visibility,
        imageUrls,
        contactEmail: contactEmail.trim() || undefined,
        displayName: displayName.trim() || undefined,
      });
      const { publicId, accessToken } = res.data ?? {};
      if (!publicId) throw new Error("invalid response");
      if (visibility === "PRIVATE" && accessToken && !isLoggedIn) {
        const link = `${window.location.origin}/support/tickets/${publicId}?token=${encodeURIComponent(accessToken)}`;
        setPrivateLink(link);
        try {
          localStorage.setItem(`support_token_${publicId}`, accessToken);
        } catch {
          /* ignore */
        }
        return;
      }
      router.push(`/support/tickets/${publicId}`);
    } catch (err) {
      setError(submitErrorToThai(err instanceof Error ? err.message : ""));
    } finally {
      setSubmitting(false);
    }
  }

  if (privateLink) {
    const ticketPath = privateLink.replace(window.location.origin, "");
    return (
      <div
        className={`${CARD_CLASS} space-y-5 p-6`}
        role="status"
        aria-live="polite"
      >
        <div className="flex flex-col items-center text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-aera-greenlight/40">
            <Check
              className="h-7 w-7 text-aera-green"
              strokeWidth={2.5}
              aria-hidden
            />
          </span>
          <h2 className="mt-4 text-lg font-bold text-aera-charcoal">
            สร้างตั๋วส่วนตัวแล้ว
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-aera-gray">
            บันทึกลิงก์นี้ไว้เพื่อดูตั๋วและคำตอบจากทีมงาน
            ตั๋วนี้จะไม่แสดงบนบอร์ดสาธารณะ
          </p>
        </div>

        <div className="rounded-xl border border-aera-charcoal/8 bg-aera-offwhite px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-aera-gray">
            ลิงก์ตั๋วของคุณ
          </p>
          <p className="mt-2 break-all text-xs text-aera-charcoal">
            {privateLink}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <AeraButton
            type="button"
            variant="outline"
            size="lg"
            fullWidth
            icon={
              copied ? (
                <Check className="h-4 w-4 text-aera-green" />
              ) : (
                <Copy className="h-4 w-4" />
              )
            }
            onClick={() => void copyPrivateLink()}
          >
            {copied ? "คัดลอกแล้ว" : "คัดลอกลิงก์"}
          </AeraButton>
          <AeraButton href={ticketPath} variant="secondary" size="lg" fullWidth>
            เปิดตั๋วของฉัน
          </AeraButton>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={`${CARD_CLASS} space-y-5 p-4 md:p-6`}>
      <div className="flex items-center gap-3 text-aera-charcoal">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-aera-warmcream">
          <MessageSquarePlus className="h-5 w-5 text-aera-amber" aria-hidden />
        </span>
        <div>
          <p className="font-semibold">สร้างตั๋วใหม่</p>
          <p className="text-xs text-aera-gray">
            อธิบายปัญหาหรือไอเดียของคุณ · แนบภาพหน้าจอได้สูงสุด 5 รูป
          </p>
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-[11px] font-semibold uppercase tracking-[0.16em] text-aera-gray">
          หมวดหมู่
        </legend>
        <div className="-mx-0.5 flex flex-nowrap gap-1.5 overflow-x-auto px-0.5 pb-0.5 scrollbar-hide md:flex-wrap md:overflow-visible md:gap-2">
          {CATEGORY_OPTIONS.map(({ value, label, href, icon: Icon }) => {
            const selected = category === value;
            return (
              <AeraButton
                key={value}
                variant={selected ? "secondary" : "outline"}
                type="button"
                fontWeight="medium"
                onClick={() => {
                  if (pathname !== href) router.push(href);
                }}
                className={`shrink-0 leading-tight`}
                icon={<Icon className="h-3.5 w-3.5" aria-hidden />}
                aria-pressed={selected}
              >
                {label}
              </AeraButton>
            );
          })}
        </div>
        <p className="text-xs text-aera-gray">{activeCategory.hint}</p>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-[11px] font-semibold uppercase tracking-[0.16em] text-aera-gray">
          การมองเห็น
        </legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {VISIBILITY_OPTIONS.map(({ value, label, hint, icon: Icon }) => {
            const selected = visibility === value;
            return (
              <label
                key={value}
                className={`relative flex cursor-pointer items-start gap-3 rounded-xl border p-3.5 transition has-focus-visible:ring-2 has-focus-visible:ring-aera-amber/40 ${
                  selected
                    ? "border-aera-amber bg-aera-warmcream/50"
                    : "border-aera-lightgray bg-aera-offwhite hover:border-aera-amber/40 hover:bg-aera-warmcream/20"
                }`}
              >
                <input
                  type="radio"
                  name="visibility"
                  value={value}
                  checked={selected}
                  onChange={() => setVisibility(value)}
                  className="sr-only"
                />
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition ${
                    selected
                      ? "bg-aera-amber text-aera-white"
                      : "bg-aera-white text-aera-gray border border-aera-lightgray"
                  }`}
                >
                  <Icon className="h-4.5 w-4.5" aria-hidden />
                </span>
                <span className="min-w-0 pr-6">
                  <span className="block text-sm font-semibold text-aera-charcoal">
                    {label}
                  </span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-aera-gray">
                    {hint}
                  </span>
                </span>
                <span
                  className={`absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full border transition ${
                    selected
                      ? "border-aera-amber bg-aera-amber"
                      : "border-aera-charcoal/20 bg-aera-white"
                  }`}
                  aria-hidden
                >
                  {selected ? (
                    <Check
                      className="h-3 w-3 text-aera-white"
                      strokeWidth={3}
                    />
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
        {visibility === "PRIVATE" ? (
          <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-950">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              สำหรับการชำระเงิน การเรียกเก็บเงิน หรือข้อมูลบัญชีที่ละเอียดอ่อน
              กรุณาใช้ตั๋วส่วนตัวและใส่อีเมลที่ผูกกับบัญชีของคุณ
            </span>
          </p>
        ) : null}
      </fieldset>

      <div className="rounded-xl border border-aera-lightgray/80 bg-aera-offwhite p-4 space-y-4">
        <div className="space-y-1">
          <label
            htmlFor="email"
            className="text-sm font-semibold text-aera-charcoal"
          >
            อีเมล {emailRequired ? "(จำเป็น)" : "(ไม่บังคับ)"}
          </label>
          <input
            id="email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            readOnly={isLoggedIn && Boolean(readEmailFromStorage())}
            placeholder="อีเมลของคุณ"
            className="w-full rounded-xl border border-aera-charcoal/15 bg-aera-white px-4 py-3 text-sm text-aera-charcoal placeholder:text-aera-gray outline-none transition focus:border-aera-amber focus:ring-2 focus:ring-aera-amber/20 disabled:bg-aera-white/80"
          />
          {isLoggedIn ? (
            <p className="text-xs text-aera-gray">
              ดึงจากบัญชีที่เข้าสู่ระบบอยู่
            </p>
          ) : null}
        </div>

        {!isLoggedIn ? (
          <div className="space-y-1">
            <label
              htmlFor="name"
              className="text-sm font-semibold text-aera-charcoal"
            >
              ชื่อที่แสดง (ไม่บังคับ)
            </label>
            <input
              id="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded-xl border border-aera-charcoal/15 bg-aera-white px-4 py-3 text-sm text-aera-charcoal placeholder:text-aera-gray outline-none transition focus:border-aera-amber focus:ring-2 focus:ring-aera-amber/20"
              placeholder="ไม่ระบุตัวตน"
            />
          </div>
        ) : null}

        <div className="space-y-1">
          <div className="flex items-baseline justify-between gap-2">
            <label
              htmlFor="title"
              className="text-sm font-semibold text-aera-charcoal"
            >
              หัวข้อ
            </label>
            <span
              className={`text-xs tabular-nums ${titleOk ? "text-aera-green" : "text-aera-gray"}`}
              aria-live="polite"
            >
              {title.trim().length}/3+
            </span>
          </div>
          <input
            id="title"
            required
            minLength={3}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="สรุปปัญหาหรือคำขอสั้น ๆ"
            className="w-full rounded-xl border border-aera-charcoal/15 bg-aera-white px-4 py-3 text-sm text-aera-charcoal placeholder:text-aera-gray outline-none transition focus:border-aera-amber focus:ring-2 focus:ring-aera-amber/20"
          />
        </div>

        <div className="space-y-1">
          <div className="flex items-baseline justify-between gap-2">
            <label
              htmlFor="body"
              className="text-sm font-semibold text-aera-charcoal"
            >
              รายละเอียด
            </label>
            <span
              className={`text-xs tabular-nums ${bodyOk ? "text-aera-green" : "text-aera-gray"}`}
              aria-live="polite"
            >
              {body.trim().length}/10+
            </span>
          </div>
          <textarea
            id="body"
            required
            minLength={10}
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="อธิบายขั้นตอนที่ทำ สิ่งที่คาดหวัง และสิ่งที่เกิดขึ้นจริง"
            className="w-full resize-y rounded-xl border border-aera-charcoal/15 bg-aera-white px-4 py-3 text-sm text-aera-charcoal placeholder:text-aera-gray outline-none transition focus:border-aera-amber focus:ring-2 focus:ring-aera-amber/20"
          />
        </div>

        <div>
          <p className="mb-2 text-sm font-semibold text-aera-charcoal">
            รูปภาพ (ไม่บังคับ)
          </p>
          <SupportImageUploader imageUrls={imageUrls} onChange={setImageUrls} />
        </div>
      </div>

      {error ? (
        <p
          className="rounded-lg bg-aera-redlight px-3 py-2 text-sm text-aera-red"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      <AeraButton
        type="submit"
        variant="primary"
        fontWeight="medium"
        fullWidth
        loading={submitting}
        disabled={!canSubmit}
      >
        {submitting ? "กำลังส่ง…" : "ส่งตั๋ว"}
      </AeraButton>
    </form>
  );
}
